import React, { useEffect, useState } from 'react';
import { 
  FileText, 
  Users, 
  TrendingUp, 
  ArrowUpRight,
  Clock,
  ExternalLink
} from 'lucide-react';
import { emendaService } from '../services/emendaService';
import { Emenda } from '../types/emenda';

export const Dashboard = () => {
  const [stats, setStats] = useState({
    totalEmendas: 0,
    totalValue: 0,
    recentEmendas: [] as Emenda[],
    loading: true
  });

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const emendas = await emendaService.getAll();
        const totalValue = emendas.reduce((acc, curr) => acc + curr.valor, 0);
        setStats({
          totalEmendas: emendas.length,
          totalValue,
          recentEmendas: emendas.slice(0, 5),
          loading: false
        });
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setStats(prev => ({ ...prev, loading: false }));
      }
    };

    fetchStats();
  }, []);

  const statCards = [
    { 
      label: 'Total de Emendas', 
      value: stats.totalEmendas.toString(), 
      icon: FileText, 
      color: 'bg-blue-500',
      trend: '+12%' 
    },
    { 
      label: 'Valor Total', 
      value: new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(stats.totalValue), 
      icon: TrendingUp, 
      color: 'bg-emerald-500',
      trend: '+8%' 
    },
    { 
      label: 'Municípios', 
      value: '2', 
      icon: Users, 
      color: 'bg-amber-500',
      trend: '+0%' 
    }
  ];

  return (
    <div className="space-y-10">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Dashboard Overview</h1>
        <p className="text-slate-500 mt-1 font-medium">Bem-vindo ao painel administrativo da APM.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {statCards.map((stat, idx) => (
          <div key={idx} className="bg-white p-6 rounded-[2rem] border border-slate-200 shadow-sm hover:shadow-md transition-shadow group">
            <div className="flex justify-between items-start">
              <div className={`${stat.color} p-3 rounded-2xl text-white shadow-lg shadow-inherit/20 group-hover:scale-110 transition-transform`}>
                <stat.icon size={24} />
              </div>
              <span className="flex items-center gap-1 text-xs font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-full">
                <ArrowUpRight size={14} />
                {stat.trend}
              </span>
            </div>
            <div className="mt-6">
              <p className="text-sm font-bold text-slate-400 uppercase tracking-widest">{stat.label}</p>
              <h3 className="text-2xl font-black text-slate-900 mt-1">
                {stats.loading ? '...' : stat.value}
              </h3>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Activity & Quick Links */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Recent Emendas */}
        <div className="lg:col-span-2 bg-white rounded-[2.5rem] border border-slate-200 shadow-sm overflow-hidden flex flex-col">
          <div className="p-8 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-xl font-bold text-slate-900">Emendas Recentes</h3>
            <button className="text-blue-600 text-sm font-bold hover:underline">Ver Todas</button>
          </div>
          <div className="flex-1 overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-slate-50 text-slate-400 text-[10px] font-black uppercase tracking-[0.2em]">
                  <th className="px-8 py-4">Título</th>
                  <th className="px-8 py-4">Valor</th>
                  <th className="px-8 py-4 text-right">Data</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stats.loading ? (
                  Array(5).fill(0).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      <td className="px-8 py-4"><div className="h-4 bg-slate-100 rounded w-32" /></td>
                      <td className="px-8 py-4"><div className="h-4 bg-slate-100 rounded w-20" /></td>
                      <td className="px-8 py-4 text-right"><div className="h-4 bg-slate-100 rounded w-24 ml-auto" /></td>
                    </tr>
                  ))
                ) : stats.recentEmendas.length > 0 ? (
                  stats.recentEmendas.map((emenda) => (
                    <tr key={emenda.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-8 py-5">
                        <p className="font-bold text-slate-900 text-sm">{emenda.titulo}</p>
                        <p className="text-xs text-slate-500 font-medium">{emenda.area_investimento}</p>
                      </td>
                      <td className="px-8 py-5">
                        <span className="text-sm font-black text-slate-700">
                          {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(emenda.valor)}
                        </span>
                      </td>
                      <td className="px-8 py-5 text-right">
                        <span className="text-xs font-bold text-slate-400 uppercase tracking-widest">
                          {new Date(emenda.data_publicacao).toLocaleDateString('pt-BR')}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={3} className="px-8 py-12 text-center text-slate-400 font-medium">
                      Nenhuma emenda registrada ainda.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Quick Links / Status */}
        <div className="space-y-6">
          <div className="bg-blue-600 rounded-[2.5rem] p-8 text-white shadow-xl shadow-blue-600/20 relative overflow-hidden group">
            <div className="absolute -right-10 -bottom-10 w-40 h-40 bg-white/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
            <h4 className="text-xl font-bold mb-4 relative z-10">Novo Lançamento?</h4>
            <p className="text-blue-100 text-sm font-medium mb-6 leading-relaxed relative z-10">
              Mantenha o sistema atualizado publicando as novas emendas parlamentares assim que recebidas.
            </p>
            <a 
              href="/admin/emendas/nova"
              className="inline-flex items-center gap-2 bg-white text-blue-600 px-6 py-3 rounded-2xl font-black text-sm hover:bg-blue-50 transition-all relative z-10 shadow-lg"
            >
              Criar Emenda
              <ArrowUpRight size={18} />
            </a>
          </div>

          <div className="bg-white border border-slate-200 rounded-[2.5rem] p-8 shadow-sm">
            <h4 className="text-lg font-bold text-slate-900 mb-6 flex items-center gap-2">
              <Clock size={20} className="text-blue-600" />
              Estado do Sistema
            </h4>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-500">Banco de Dados</span>
                <span className="w-2 h-2 bg-emerald-500 rounded-full shadow-lg shadow-emerald-500/20"></span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-500">Storage API</span>
                <span className="w-2 h-2 bg-emerald-500 rounded-full shadow-lg shadow-emerald-500/20"></span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-slate-500">Site Público</span>
                <span className="w-2 h-2 bg-emerald-500 rounded-full shadow-lg shadow-emerald-500/20"></span>
              </div>
            </div>
            <hr className="my-6 border-slate-100" />
            <a href="/" target="_blank" className="flex items-center gap-2 text-blue-600 text-sm font-bold hover:underline">
              Visualizar Site Público
              <ExternalLink size={16} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
